<template>
  <div class="ai-checker-container">
    <!-- 头部标题 -->
    <div class="header">
      <h1>Turnitin & AI Checker</h1>
    </div>
    
    <!-- 主要内容区域 - 版本选择器和上传区域左右排列 -->
    <div class="main-content">
      <!-- 版本选择器 -->
      <div class="version-selector">
        <el-button 
          class="version-btn"
          :class="selectedVersion === 'international' ? 'international-version' : 'uk-version'"
          @click="selectVersion('international')"
        >
          International Version
        </el-button>
        <el-button 
          class="version-btn"
          :class="selectedVersion === 'uk' ? 'international-version' : 'uk-version'"
          @click="selectVersion('uk')"
        >
          UK Version
        </el-button>
      </div>
      
      <!-- 上传区域 -->
      <div class="upload-section">
      <div class="upload-box">
        <!-- <p class="upload-text">Click or drag and drop multiple files here.</p> -->
        <p class="upload-note">Click or drag and drop multiple files here.</br>Files containing personal information or any </br>other banned files are prohibited.</p>
        
        <el-upload
          class="upload-demo"
          drag
          action="#"
          :before-upload="beforeUpload"
          multiple
          accept=".doc,.docx,.pdf"
        >
          <div class="upload-area">
            <i class="el-icon-folder upload-icon"></i>
            <div class="el-upload__text">
              <em><i class="el-icon-folder-opened"></i> Drop Files</em>
            </div>
            <div class="el-upload__tip">
              DOC, PDF Format Only
            </div>
          </div>
        </el-upload>
      </div>
      
      <!-- 已上传文件列表 -->
      <div class="file-list" v-if="uploadedFiles.length > 0">
        <el-divider content-position="left">Uploaded Files</el-divider>
        <div class="file-items">
          <el-tag 
            v-for="(file, index) in uploadedFiles" 
            :key="index" 
            closable 
            :disable-transitions="false"
            class="file-tag"
            @close="removeFile(index)"
          >
            {{ file.name }}
          </el-tag>
        </div>
        <el-button 
          type="text" 
          class="clear-btn"
          @click="clearFiles"
          v-if="uploadedFiles.length > 0"
        >
          Clear All
        </el-button>
      </div>
    </div>
    
    <!-- 闭合主要内容区域 -->
    </div>
    
    <!-- 选项区域 -->
    <div class="options-section">
      <el-checkbox v-model="excludeReference" class="option-checkbox">Excluding Reference</el-checkbox>
      <el-checkbox v-model="aiCheck" class="option-checkbox">AI Check</el-checkbox>
    </div>
    
    <!-- 提交按钮 -->
    <div class="submit-section">
      <el-button 
        type="primary" 
        size="large" 
        class="submit-btn"
        @click="submit"
        :loading="loading"
        :disabled="loading || uploadedFiles.length === 0"
      >
        Submit
      </el-button>
    </div>
    
    <!-- 结果区域 -->
    <div class="result-section">
      <h2>Testing Result</h2>
      <div class="result-message" v-if="!result">
        No plagiarism report available yet.
      </div>
      <div class="result-content" v-else>
        <div class="result-item">
          <span class="result-label">Plagiarism Rate:</span>
          <span class="result-value plagiarism-rate">{{ result.plagiarismRate }}</span>
        </div>
        <div class="result-item" v-if="aiCheck">
          <span class="result-label">AI Generated Content:</span>
          <span class="result-value ai-rate">{{ result.aiGeneratedContent }}</span>
        </div>
        <div class="result-detail">
          {{ result.details }}
        </div>
        <el-button 
          type="success" 
          class="download-btn"
          @click="downloadReport"
          :disabled="!result || !result.fileId || !result.hasFeedback"
        >
          Download Report
        </el-button>
      </div>
    </div>
  
    <div class="history-section">
        <el-divider content-position="left">My Upload History</el-divider>
        
        <el-table
          v-loading="historyLoading"
          :data="historyFiles"
          style="width: 100%; margin-bottom: 20px;"
        >
          <el-table-column
            prop="original_filename"
            label="File Name"
            min-width="200"
          />
          <el-table-column
            prop="version"
            label="Version"
            width="120"
          />
          <el-table-column
            prop="upload_time"
            label="Upload Time"
            width="180"
          />
          <el-table-column
            prop="has_feedback"
            label="Feedback Status"
            width="120"
          >
            <template #default="scope">
              <el-tag :type="scope.row.has_feedback ? 'success' : 'warning'">
                {{ scope.row.has_feedback ? 'Available' : 'Pending' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column
            label="Actions"
            width="200"
          >
            <template #default="scope">
              <el-button
                type="text"
                size="small"
                @click="downloadHistoryFile(scope.row)"
              >
                Download File
              </el-button>
              <el-button
                type="text"
                size="small"
                @click="downloadHistoryFeedback(scope.row)"
                :disabled="!scope.row.has_feedback"
              >
                Download Feedback
              </el-button>
            </template>
          </el-table-column>
        </el-table>
        
        <el-pagination
          :current-page="historyPagination.currentPage"
          :page-size="historyPagination.pageSize"
          :page-sizes="[5, 10, 20]"
          layout="total, sizes, prev, pager, next"
          :total="historyPagination.total"
          @size-change="handleHistorySizeChange"
          @current-change="handleHistoryCurrentChange"
        />
    </div>
  </div>
  </template>

<script>
import { downloadAiReport, uploadFile, getFeedbackStatus, downloadFeedback } from '../../api/tools/upload';
import {
  getUserInfo
} from '@/api/api.js';
// import { downloadFileItem } from '../../api/api';

export default {
  name: 'AIChecker',
  data() {
    return {
      selectedVersion: 'international',
      excludeReference: false,
      aiCheck: false,
      uploadedFiles: [],
      loading: false,
      progress: 0,
      result: null,
      historyFiles: [],
      historyLoading: false,
      historyPagination: {
        currentPage: 1,
        pageSize: 10,
        total: 0
        },
      // 当前用户ID
      userId: '',
      // 用户信息对象
      userInfo: {}
    };
  },
  methods: {
    async getUserInfo() {
      try {
        let result = await getUserInfo();
        console.log('getUserInfo result', result);
        // 存储用户信息
        this.userInfo = result;
        // 设置用户ID
        this.userId = result.id;
        console.log('Current user ID:', this.userId);
      } catch (error) {
        console.error('Failed to get user info:', error);
        this.$message.error('Failed to get user info');
      }
    },
    selectVersion(version) {
      this.selectedVersion = version;
    },
    beforeUpload(file) {
      const isDoc = file.type === 'application/msword' || 
                    file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
                    file.type === 'application/pdf';
      
      if (!isDoc) {
        this.$message.error('Only DOC, DOCX and PDF files are allowed!');
        return false;
      }
      
      this.uploadedFiles.push(file);
      return false; // Prevent auto-upload
    },
    async submit() {
      if (this.uploadedFiles.length === 0) {
        this.$message.warning('Please upload at least one file before submitting');
        return;
      }
      
      // 检查用户ID是否已设置
      if (!this.userId) {
        console.warn('User ID is not set, trying to get user info...');
        await this.getUserInfo();
        // 如果仍然没有用户ID，显示错误信息
        if (!this.userId) {
          this.$message.error('Failed to get user information, cannot submit files');
          return;
        }
      }
      
      this.loading = true;
      this.progress = 0;
      
      try {
        // 对每个文件进行上传
        for (let i = 0; i < this.uploadedFiles.length; i++) {
          const file = this.uploadedFiles[i];
          
          // 设置上传参数
          const params = {
            category: 'ai_checker',
            version: this.selectedVersion,
            excludeReference: this.excludeReference,
            aiCheck: this.aiCheck,
            userId: this.userId
          };
          
          // 上传文件
          const response = await uploadFile(file, params);  
          // 更新进度
          this.progress = Math.round(((i + 1) / this.uploadedFiles.length) * 100);          
          // 处理上传结果
          if (response && response.success) {
            this.$message.success(`File ${file.name} uploaded successfully`)
          
            this.result = {
              fileId: response.fileId,
              hasFeedback: false
            };
            
            // 开始轮询检查反馈状态
            this.checkFeedbackStatus(response.fileId);
          } else {
            this.$message.error(`File ${file.name} upload failed: ${response.message || 'Unknown error'}`);
          }
        }
      } catch (error) {
        console.error('File upload failed:', error);
        this.$message.error('File upload failed, please try again later');
      } finally {
        this.loading = false;
      }
    },
    
    // 移除文件
    removeFile(index) {
      this.uploadedFiles.splice(index, 1);
    },
    
    // 清空文件列表
    clearFiles() {
      this.uploadedFiles = [];
      this.result = null;
    },
    
    // 下载检测报告（如果有的话）
    async downloadReport() {
      if (!this.result || !this.result.fileId) {
        this.$message.warning('No report available for download');
        return;
      }
      
      try {
        // 调用下载API，获取blob对象
        const response = await downloadAiReport(this.result.fileId);
        
        // 创建blob URL
        const blob = new Blob([response.data], { 
          type: 'application/pdf'  // 根据实际文件类型调整
        });
        const url = window.URL.createObjectURL(blob);
        
        // 创建下载链接并触发下载
        const link = document.createElement('a');
        link.href = url;
        // 提取原始文件名并构造报告文件名
        const originalName = this.uploadedFiles[0].name.replace(/\.[^/.]+$/, '');
        link.download = `Check_Report_${originalName}.pdf`;
        document.body.appendChild(link);
        link.click();
        
        // 清理资源
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        this.$message.success('Report downloaded successfully');
      } catch (error) {
        console.error('Report download failed:', error);
        this.$message.error('Report download failed, please try again later');
      }
    },
    
    // 检查反馈状态
    async checkFeedbackStatus(fileId) {
      // 每5秒检查一次反馈状态
      const interval = setInterval(async () => {
        try {
          const response = await getFeedbackStatus(fileId);
          
          if (response.success && response.hasFeedback) {
            // 更新结果状态
            this.result.hasFeedback = true;
            this.result.feedbackId = response.feedbackId;
            // 停止轮询
            clearInterval(interval);
            this.$message.success('Report is ready for download');
          }
        } catch (error) {
          console.error('Failed to check feedback status:', error);
        }
      }, 5000); // 5秒轮询一次
    },
    async loadHistoryFiles() {
      // 检查用户ID是否已设置
      if (!this.userId) {
        console.warn('User ID is not set, trying to get user info...');
        await this.getUserInfo();
        // 如果仍然没有用户ID，显示错误信息
        if (!this.userId) {
          this.$message.error('Failed to get user information, cannot load history files');
          return;
        }
      }
      
      this.historyLoading = true;
      try {
        const response = await fetch(`/api/get-ai-files?page=${this.historyPagination.currentPage}&size=${this.historyPagination.pageSize}&userId=${this.userId}`);
        const res = await response.json();
        
        if (res.success) {
          this.historyFiles = res.data || [];
          this.historyPagination.total = res.total || 0;
        } else {
          this.$message.error(res.message || 'Failed to load history files');
        }
      } catch (error) {
        console.error('Failed to load history files:', error);
        this.$message.error('Failed to load history files');
      } finally {
        this.historyLoading = false;
      }
    },
    // 分页大小变化
    handleHistorySizeChange(size) {
      this.historyPagination.pageSize = size;
      this.historyPagination.currentPage = 1;
      this.loadHistoryFiles();
    },
    
    // 当前页码变化
    handleHistoryCurrentChange(current) {
      this.historyPagination.currentPage = current;
      this.loadHistoryFiles();
    },
    downloadHistoryFile(file) {
      try {
        const downloadUrl = `/download-ai-report?fileId=${file.file_id}`;
        window.open(downloadUrl, '_blank');
      } catch (error) {
        console.error('Failed to download file:', error);
        this.$message.error('Failed to download file');
      }
    },
    async downloadHistoryFeedback(file) {
      try {
        // 先获取反馈ID
        const response = await fetch(`/get-feedback-status?fileId=${file.file_id}`);
        const result = await response.json();
        
        if (result.success && result.feedbackId) {
          const downloadUrl = `/download-feedback?feedbackId=${result.feedbackId}`;
          window.open(downloadUrl, '_blank');
        } else {
          this.$message.error('No feedback available for this file');
        }
      } catch (error) {
        console.error('Failed to download feedback:', error);
        this.$message.error('Failed to download feedback');
      }
    },
  },
  async mounted() {
    // 先获取用户信息
    await this.getUserInfo();
    // 然后加载历史文件
    this.loadHistoryFiles();
  }
}

</script>

<style lang="scss" scoped>
.ai-checker-container {
    background-color: white;
    border-radius: 8px;
    padding: 40px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
    max-width: 1200px;
    margin: 0 auto;
    
    .header {
      text-align: center;
      margin-bottom: 30px;
      
      h1 {
        font-size: 28px;
        font-weight: 600;
        color: #333;
        margin-bottom: 20px;
        text-align: left;
        padding-block-end: 2%;
      }
    }
    
    /* 主要内容区域flex布局 */
  .main-content {
    display: flex;
    gap: 20px;
    margin-bottom: 20px;
    align-items: flex-start;
    box-sizing: border-box;
  }
  
  /* 移除相邻按钮的默认左边距 */
  .el-button+.el-button {
    margin-left: 0 !important;
  }
  
  .version-selector {
    width: 250px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    align-items: flex-start;
    
    .version-btn {
      width: 250px;
      padding: 12px 20px;
      font-size: 16px;
      border-radius: 10px; /* 增大圆角，使其与图片一致 */
      transition: all 0.3s ease;
      box-sizing: border-box;
      text-align: center;
      height: auto;
      line-height: normal;
      font-weight: 500;
    }
    
    .international-version {
      background-color: white;
      color: #577CF2;
      font-weight:1000;
      border-radius: 10px;
      border: 1px solid #577CF2;
      // box-shadow: 0 2px 8px rgba(64, 158, 255, 0.3);
    }
    
    .uk-version {
      background-color: white;
      border-color: #dcdfe6;
      color: #909399; /* 使用更浅的灰色，使其与图片一致 */
      border-width: 2px;
      font-weight: 1000;
    }
  }
  
  .upload-section {
    flex: 1;
    background-color: #f5f7fa;
    border: 1px solid #e4e7ed;
    border-radius: 8px;
    padding: 30px;
    margin-bottom: 0;
    box-sizing: border-box;
    min-width: 0; /* 防止flex子元素溢出 */
      
      .upload-box {     
        text-align: center;
        width: 100%;
      
      .upload-text {
        font-size: 16px;
        color: #606266;
        margin-bottom: 8px;
        font-weight: 500;
      }
      
      .upload-note {
        color: #868688;
        font-weight: 400;
        font-size: 15px;
        font-style: normal;
        line-height: 1.5;
        text-decoration: none;
        text-align: center;
        font-family: PingFangSC-regular;
        margin-top: 25px;
      }
      
      .upload-area {
        .upload-icon {
          display: none;
        }
        
        .el-upload__text {
          font-size: 14px;
          color: #303133;
          margin-bottom: 8px;
          
          em {
            color: white;
            font-style: normal;
            padding: 12px 30px;
            font-size: 20px;
            display: inline-block;
            margin-top: 50px;
            font-weight: 1000;
            position: relative;
            align-items: center;
            gap: 10px;
            transition: all 0.3s ease;
            border-radius: 10px;
            background-color: #577CF2;
          }
          
          /* 图标使用Element UI内置的class方式 */
        }
        
        .el-upload__tip {
          font-size: 14px;
          color: #909399;
          margin-top: 15px;
        }
      }
    }
    
    .file-list {
      margin-top: 20px;
      
      .file-items {
        margin: 10px 0;
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
      }
      
      .file-tag {
        max-width: 200px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        padding: 6px 10px;
        background-color: #f0f9ff;
        border-color: #a0cfff;
        color: #409eff;
      }
      
      .clear-btn {
        color: #f56c6c;
        margin-top: 10px;
        padding: 0;
        
        &:hover {
          color: #f78989;
        }
      }
      }
    }
    
    /* 主要内容区域结束 */
    
    .options-section {
      display: flex;
      justify-content: flex-start;
      margin-bottom: 20px;
      gap: 40px;
      margin-top: 0; /* 移除上边距，与upload-section顶部对齐 */
      padding-left: calc(250px + 20px); /* 与upload-section左侧对齐，考虑版本选择器宽度和main-content的gap */
      padding-top: 0; /* 移除顶部内边距，保持与main-content顶部对齐 */
    
    .option-checkbox {
      font-size: 14px;
      
      ::v-deep .el-checkbox__label {
        color: #9B9B9B;
      }
    }
  }
  
  .submit-section {
      display: flex;
      justify-content: flex-end;
      margin-bottom: 40px;
      margin-top: 10px;
      padding-left: 0; /* 移除左内边距，因为版本选择器现在水平排列 */
      
      .submit-btn {
        padding: 12px 60px;
        font-size: 16px;
        border-radius: 20px; /* 增大圆角，使其更圆润 */
        background-color: #577CF2;
        border-color: #577CF2;
        color: white;
        font-weight: 520;
        
        &:hover {
          background-color: #66b1ff;
          border-color: #66b1ff;
        }
    }
  }
  
  .result-section {
    border-top: 1px solid #e4e7ed;
    padding-top: 20px;
    margin-top: 20px;
    padding-left: 0; /* 移除左内边距，因为版本选择器现在水平排列 */
    
    h2 {
      font-size: 25px;
      font-weight: 700;
      font-family: Roboto-bold;
      color: #577CF2;
      margin-bottom: 15px;
      text-align: left;
    }
    
    .result-message {
      font-size: 14px;
      color: #909399;
      padding: 20px;
      background-color: #fafafa;
      border-radius: 4px;
      border: 1px dashed #dcdfe6;
      margin-top: 10px;
    }
  }
    
    /* 修改el-upload样式，设置透明背景且移除虚线 */
    ::v-deep .el-upload {
      border: none !important;
      background-color: transparent !important;
    }
    
    ::v-deep .el-upload-dragger {
      background-color: transparent !important;
      border: none !important;
    }
    
    ::v-deep .el-upload:focus-within {
      border-color: transparent !important;
      box-shadow: none !important;
    }
  }
</style>